import { combineActions, handleActions } from 'redux-actions';
import {
    DELETE_BANK_ACCOUNT_FAIL,
    DELETE_BANK_ACCOUNT_FAIL_ACTION,
    DELETE_BANK_ACCOUNT_REQUEST,
    DELETE_BANK_ACCOUNT_REQUEST_ACTION,
    DELETE_BANK_ACCOUNT_SUCCESS,
    DELETE_BANK_ACCOUNT_SUCCESS_ACTION,
    LOAD_BANK_ACCOUNTS_FAIL,
    LOAD_BANK_ACCOUNTS_FAIL_ACTION,
    LOAD_BANK_ACCOUNTS_REQUEST,
    LOAD_BANK_ACCOUNTS_REQUEST_ACTION,
    LOAD_BANK_ACCOUNTS_SUCCESS,
    LOAD_BANK_ACCOUNTS_SUCCESS_ACTION,
    POST_BANK_ACCOUNT_FAIL,
    POST_BANK_ACCOUNT_FAIL_ACTION,
    POST_BANK_ACCOUNT_REQUEST,
    POST_BANK_ACCOUNT_REQUEST_ACTION,
    POST_BANK_ACCOUNT_SUCCESS,
    POST_BANK_ACCOUNT_SUCCESS_ACTION,
} from './actions';
import { deleteBankAccount, fetchBankAccounts, postAddBankAccount } from '@/redux/api/payment';
import { getAuthToken, getBidderId } from './user';
import { BankAccount } from '@liveauctioneers/caterwaul-components/types/BankAccount';
import { createSelector } from 'reselect';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import paymentProviders from '../../enums/paymentProviders';

export type State = {
    readonly bankAccounts: BankAccount[];
    readonly bankAccountsLoaded?: Date;
    readonly bankAccountsLoading: boolean;
    readonly errorMessage: string | null;
};

export const DEFAULT_STATE: State = {
    bankAccounts: [],
    bankAccountsLoaded: undefined,
    bankAccountsLoading: false,
    errorMessage: null,
};

export const bankAccount = handleActions(
    {
        [combineActions(DELETE_BANK_ACCOUNT_FAIL, LOAD_BANK_ACCOUNTS_FAIL, POST_BANK_ACCOUNT_FAIL)]: (
            state: State,
            action: DELETE_BANK_ACCOUNT_FAIL_ACTION | LOAD_BANK_ACCOUNTS_FAIL_ACTION | POST_BANK_ACCOUNT_FAIL_ACTION
        ): State => {
            return {
                ...state,
                bankAccountsLoading: false,
                errorMessage: action.payload,
            };
        },
        [combineActions(DELETE_BANK_ACCOUNT_REQUEST, LOAD_BANK_ACCOUNTS_REQUEST)]: (state: State) => {
            return {
                ...state,
                bankAccountsLoaded: undefined,
                bankAccountsLoading: true,
                errorMessage: null,
            };
        },
        [DELETE_BANK_ACCOUNT_SUCCESS]: (state: State) => {
            return {
                ...state,
                bankAccounts: [],
                errorMessage: null,
            };
        },
        [LOAD_BANK_ACCOUNTS_SUCCESS]: (state: State, action: LOAD_BANK_ACCOUNTS_SUCCESS_ACTION) => {
            return {
                ...state,
                bankAccounts: action.payload || [],
                bankAccountsLoaded: new Date(),
                bankAccountsLoading: false,
                errorMessage: null,
            };
        },
        [POST_BANK_ACCOUNT_SUCCESS]: (state: State, action: POST_BANK_ACCOUNT_SUCCESS_ACTION) => {
            return {
                ...state,
                bankAccounts: [...state.bankAccounts, action.payload],
                errorMessage: null,
            };
        },
    },
    DEFAULT_STATE
);

const stateSelector = (state: GlobalState) => state.bankAccount;

export const getBankAccounts = createSelector(stateSelector, (state) => state.bankAccounts);

export const getUiBankAccount: (state: GlobalState) => State = createSelector(stateSelector, (state) => state);

export const fetchBankAccountsIfNeeded = () => async (dispatch: Function, getState: Function) => {
    //TODO if needed logic
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);

        dispatch({
            type: LOAD_BANK_ACCOUNTS_REQUEST,
        } as LOAD_BANK_ACCOUNTS_REQUEST_ACTION);

        const response = await fetchBankAccounts({ authToken, bidderId, deployment });
        return dispatch({
            payload: response.payload,
            type: LOAD_BANK_ACCOUNTS_SUCCESS,
        } as LOAD_BANK_ACCOUNTS_SUCCESS_ACTION);
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: LOAD_BANK_ACCOUNTS_FAIL,
        } as LOAD_BANK_ACCOUNTS_FAIL_ACTION);
    }
};

type SubmitPostBankAccountParams = {
    accountNumber: string;
    bankAccountName: string;
    firstName: string;
    lastName: string;
    providerId: number;
    routingNumber: string;
};

export const submitPostBankAccount = ({
    accountNumber,
    bankAccountName,
    firstName,
    lastName,
    routingNumber,
}: SubmitPostBankAccountParams) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);

        dispatch({
            type: POST_BANK_ACCOUNT_REQUEST,
        } as POST_BANK_ACCOUNT_REQUEST_ACTION);

        const response = await postAddBankAccount({
            accountNumber,
            authToken,
            bankAccountName,
            bidderId,
            deployment,
            firstName,
            lastName,
            providerId: paymentProviders.PAYRIX,
            routingNumber,
        });
        dispatch({
            payload: response.payload,
            type: POST_BANK_ACCOUNT_SUCCESS,
        } as POST_BANK_ACCOUNT_SUCCESS_ACTION);
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: POST_BANK_ACCOUNT_FAIL,
        } as POST_BANK_ACCOUNT_FAIL_ACTION);
        throw error;
    }
};

export const submitDeleteBankAccount = (bankAccountId: string) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);

        dispatch({
            type: DELETE_BANK_ACCOUNT_REQUEST,
        } as DELETE_BANK_ACCOUNT_REQUEST_ACTION);

        await deleteBankAccount({ authToken, bankAccountId, bidderId, deployment });

        dispatch({ type: DELETE_BANK_ACCOUNT_SUCCESS } as DELETE_BANK_ACCOUNT_SUCCESS_ACTION);

        dispatch(fetchBankAccountsIfNeeded());
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: DELETE_BANK_ACCOUNT_FAIL,
        } as DELETE_BANK_ACCOUNT_FAIL_ACTION);
    }
};
